/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Switch, Tooltip } from '@mui/material';
import React from 'react';
import { Info } from '@opengeoweb/theme';
import { usePlugin } from '../PluginContext';

interface OverlayPluginMenuListItemProps {
  pluginId: string;
  pluginName: string;
  isActive: boolean;
  onToggle: (pluginId: string, isActive: boolean) => void;
}

export const OverlayPluginMenuListItem = ({
  pluginId,
  pluginName,
  isActive,
  onToggle,
}: OverlayPluginMenuListItemProps): React.JSX.Element => {
  const { loadPlugin, unloadPlugin } = usePlugin();
  const centerItems = {
    display: 'flex',
    justifyItems: 'center',
    alignItems: 'center',
  };

  return (
    <Grid
      container
      sx={{ width: '100%', height: '30px', justifyContent: 'space-between' }}
    >
      <Grid
        size={{ xs: 8 }}
        sx={{
          justifyContent: 'flex-start',
          overflow: 'hidden',
          ...centerItems,
        }}
      >
        <Tooltip placement="top" title={pluginName}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {pluginName}
          </span>
        </Tooltip>
      </Grid>
      <Grid
        size={{
          xs: 1,
        }}
        sx={{ ...centerItems }}
      >
        <Info />
      </Grid>
      <Grid
        size={{ xs: 2 }}
        sx={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <Switch
          checked={isActive}
          onChange={(_e, checked) => {
            if (checked) {
              void loadPlugin(pluginId);
            } else {
              unloadPlugin(pluginId);
            }
            onToggle(pluginId, checked);
          }}
        />
      </Grid>
    </Grid>
  );
};
